/**
 * Base CSS styles for the widget (injected into Shadow DOM)
 */

export const BASE_STYLES = `
  :host {
    --sitegpt-primary: #0066cc;
    --sitegpt-primary-hover: #0052a3;
    --sitegpt-primary-light: #e6f0ff;
    --sitegpt-text: #1a1a1a;
    --sitegpt-text-secondary: #666666;
    --sitegpt-bg: #ffffff;
    --sitegpt-bg-secondary: #f5f5f5;
    --sitegpt-border: #e5e7eb;
    --sitegpt-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    --sitegpt-radius: 12px;
    --sitegpt-radius-sm: 8px;
    --sitegpt-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

    all: initial;
    font-family: var(--sitegpt-font);
    font-size: 14px;
    line-height: 1.5;
    color: var(--sitegpt-text);
    box-sizing: border-box;
  }

  *, *::before, *::after {
    box-sizing: inherit;
  }

  /* Chat Bubble Button */
  .sitegpt-bubble {
    position: fixed;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--sitegpt-primary);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--sitegpt-shadow);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    z-index: 2147483647;
  }

  .sitegpt-bubble:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  }

  .sitegpt-bubble:active {
    transform: scale(0.95);
  }

  .sitegpt-bubble svg {
    width: 28px;
    height: 28px;
    fill: white;
  }

  .sitegpt-bubble.bottom-right {
    bottom: 20px;
    right: 20px;
  }

  .sitegpt-bubble.bottom-left {
    bottom: 20px;
    left: 20px;
  }

  .sitegpt-bubble-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
  }

  /* Welcome Cloud */
  .sitegpt-welcome-cloud {
    position: fixed;
    max-width: 220px;
    padding: 12px 16px;
    background: var(--sitegpt-bg);
    border-radius: var(--sitegpt-radius);
    box-shadow: var(--sitegpt-shadow);
    font-size: 14px;
    line-height: 1.4;
    color: var(--sitegpt-text);
    cursor: pointer;
    z-index: 2147483646;
    animation: sitegpt-cloud-in 0.3s ease;
  }

  .sitegpt-welcome-cloud.bottom-right {
    right: 70px;
    bottom: 70px;
  }

  .sitegpt-welcome-cloud.bottom-left {
    left: 70px;
    bottom: 70px;
  }

  .sitegpt-welcome-cloud:hover {
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  }

  .sitegpt-welcome-cloud.hiding {
    animation: sitegpt-cloud-out 0.3s ease forwards;
  }

  @keyframes sitegpt-cloud-in {
    from {
      opacity: 0;
      transform: translate(5px, 5px);
    }
    to {
      opacity: 1;
      transform: translate(0, 0);
    }
  }

  @keyframes sitegpt-cloud-out {
    from {
      opacity: 1;
      transform: translate(0, 0);
    }
    to {
      opacity: 0;
      transform: translate(5px, 5px);
    }
  }

  /* Chat Window */
  .sitegpt-window {
    position: fixed;
    width: 380px;
    height: 600px;
    max-height: calc(100vh - 100px);
    max-width: calc(100vw - 40px);
    background: var(--sitegpt-bg);
    border-radius: var(--sitegpt-radius);
    box-shadow: var(--sitegpt-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2147483647;
    opacity: 0;
    transform: translateY(20px) scale(0.95);
    transition: opacity 0.2s ease, transform 0.2s ease;
    pointer-events: none;
  }

  .sitegpt-window.open {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  .sitegpt-window.bottom-right {
    bottom: 90px;
    right: 20px;
  }

  .sitegpt-window.bottom-left {
    bottom: 90px;
    left: 20px;
  }

  /* Header */
  .sitegpt-header {
    display: flex;
    align-items: center;
    padding: 16px;
    background: var(--sitegpt-primary);
    color: white;
    gap: 12px;
  }

  .sitegpt-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .sitegpt-header-icon img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }

  .sitegpt-header-icon svg {
    width: 24px;
    height: 24px;
    fill: white;
  }

  .sitegpt-header-info {
    flex: 1;
    min-width: 0;
  }

  .sitegpt-header-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sitegpt-header-subtitle {
    font-size: 12px;
    opacity: 0.8;
    margin: 0;
  }

  .sitegpt-header-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sitegpt-header-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
  }

  .sitegpt-header-btn:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  .sitegpt-header-btn svg {
    width: 18px;
    height: 18px;
    fill: white;
  }

  .sitegpt-header-close {
    /* Additional styles for close button if needed */
  }

  /* Messages Area */
  .sitegpt-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .sitegpt-message {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: var(--sitegpt-radius-sm);
    word-wrap: break-word;
    animation: sitegpt-fade-in 0.2s ease;
  }

  @keyframes sitegpt-fade-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .sitegpt-message.user {
    align-self: flex-end;
    background: var(--sitegpt-primary);
    color: white;
    border-bottom-right-radius: 4px;
  }

  .sitegpt-message.bot {
    align-self: flex-start;
    background: var(--sitegpt-bg-secondary);
    color: var(--sitegpt-text);
    border-bottom-left-radius: 4px;
  }

  /* Typing Indicator */
  .sitegpt-typing {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    background: var(--sitegpt-bg-secondary);
    border-radius: var(--sitegpt-radius-sm);
    align-self: flex-start;
  }

  .sitegpt-typing-dot {
    width: 8px;
    height: 8px;
    background: var(--sitegpt-text-secondary);
    border-radius: 50%;
    animation: sitegpt-typing 1.4s infinite;
  }

  .sitegpt-typing-dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .sitegpt-typing-dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes sitegpt-typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.5;
    }
    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }

  /* Input Area */
  .sitegpt-input-area {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid var(--sitegpt-border);
    background: var(--sitegpt-bg);
  }

  .sitegpt-input {
    flex: 1;
    min-height: 40px;
    max-height: 120px;
    padding: 10px 14px;
    border: 1px solid var(--sitegpt-border);
    border-radius: var(--sitegpt-radius-sm);
    font-family: inherit;
    font-size: 14px;
    resize: none;
    outline: none;
    transition: border-color 0.2s ease;
  }

  .sitegpt-input:focus {
    border-color: var(--sitegpt-primary);
  }

  .sitegpt-input::placeholder {
    color: var(--sitegpt-text-secondary);
  }

  .sitegpt-send {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--sitegpt-primary);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, transform 0.1s ease;
    flex-shrink: 0;
  }

  .sitegpt-send:hover {
    background: var(--sitegpt-primary-hover);
  }

  .sitegpt-send:active {
    transform: scale(0.95);
  }

  .sitegpt-send:disabled {
    background: var(--sitegpt-border);
    cursor: not-allowed;
  }

  .sitegpt-send svg {
    width: 20px;
    height: 20px;
    fill: white;
  }

  /* Lead Form */
  .sitegpt-lead-form {
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .sitegpt-lead-form h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  .sitegpt-lead-form p {
    font-size: 14px;
    color: var(--sitegpt-text-secondary);
    margin: 0 0 16px 0;
  }

  .sitegpt-form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .sitegpt-form-field label {
    font-size: 13px;
    font-weight: 500;
    color: var(--sitegpt-text);
  }

  .sitegpt-form-field input {
    padding: 10px 14px;
    border: 1px solid var(--sitegpt-border);
    border-radius: var(--sitegpt-radius-sm);
    font-family: inherit;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease;
  }

  .sitegpt-form-field input:focus {
    border-color: var(--sitegpt-primary);
  }

  .sitegpt-form-field input::placeholder {
    color: var(--sitegpt-text-secondary);
  }

  .sitegpt-form-submit {
    padding: 12px 24px;
    background: var(--sitegpt-primary);
    color: white;
    border: none;
    border-radius: var(--sitegpt-radius-sm);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-top: 8px;
  }

  .sitegpt-form-submit:hover {
    background: var(--sitegpt-primary-hover);
  }

  .sitegpt-form-submit:disabled {
    background: var(--sitegpt-border);
    cursor: not-allowed;
  }

  .sitegpt-form-skip {
    text-align: center;
    font-size: 13px;
    color: var(--sitegpt-text-secondary);
  }

  .sitegpt-form-skip button {
    background: none;
    border: none;
    color: var(--sitegpt-primary);
    cursor: pointer;
    font-family: inherit;
    font-size: inherit;
    text-decoration: underline;
  }

  /* Welcome Message */
  .sitegpt-welcome {
    text-align: center;
    padding: 20px;
    color: var(--sitegpt-text-secondary);
    font-size: 14px;
  }

  /* Error Message */
  .sitegpt-error {
    padding: 12px 16px;
    background: #fee2e2;
    color: #dc2626;
    border-radius: var(--sitegpt-radius-sm);
    font-size: 13px;
    text-align: center;
  }

  /* Powered By */
  .sitegpt-powered {
    text-align: center;
    padding: 8px;
    font-size: 11px;
    color: var(--sitegpt-text-secondary);
    border-top: 1px solid var(--sitegpt-border);
  }

  .sitegpt-powered a {
    color: var(--sitegpt-primary);
    text-decoration: none;
  }

  .sitegpt-powered a:hover {
    text-decoration: underline;
  }

  /* Confirmation Dialog */
  .sitegpt-confirm-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    animation: sitegpt-fade-in 0.15s ease;
  }

  .sitegpt-confirm-dialog {
    background: var(--sitegpt-bg);
    border-radius: var(--sitegpt-radius);
    padding: 20px;
    margin: 16px;
    max-width: 300px;
    box-shadow: var(--sitegpt-shadow);
    animation: sitegpt-scale-in 0.15s ease;
  }

  @keyframes sitegpt-scale-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .sitegpt-confirm-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: var(--sitegpt-text);
  }

  .sitegpt-confirm-message {
    font-size: 14px;
    color: var(--sitegpt-text-secondary);
    margin: 0 0 20px 0;
    line-height: 1.5;
  }

  .sitegpt-confirm-buttons {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }

  .sitegpt-confirm-btn {
    padding: 8px 16px;
    border-radius: var(--sitegpt-radius-sm);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease, opacity 0.2s ease;
    border: none;
  }

  .sitegpt-confirm-btn.cancel {
    background: var(--sitegpt-bg-secondary);
    color: var(--sitegpt-text);
  }

  .sitegpt-confirm-btn.cancel:hover {
    background: var(--sitegpt-border);
  }

  .sitegpt-confirm-btn.confirm {
    background: var(--sitegpt-primary);
    color: white;
  }

  .sitegpt-confirm-btn.confirm:hover {
    background: var(--sitegpt-primary-hover);
  }

  /* Mobile Responsive */
  @media (max-width: 480px) {
    .sitegpt-window {
      width: 100%;
      height: 100%;
      max-height: 100vh;
      max-width: 100vw;
      border-radius: 0;
      bottom: 0;
      right: 0;
      left: 0;
    }

    .sitegpt-window.bottom-right,
    .sitegpt-window.bottom-left {
      bottom: 0;
      right: 0;
      left: 0;
    }

    .sitegpt-welcome-cloud {
      max-width: calc(100vw - 100px);
    }

    .sitegpt-welcome-cloud.bottom-right {
      right: 70px;
      bottom: 70px;
    }

    .sitegpt-welcome-cloud.bottom-left {
      left: 70px;
      bottom: 70px;
    }
  }
`;

/**
 * Generate dynamic theme CSS based on primary color
 */
export function generateThemeStyles(primaryColor: string): string {
  const darken = (color: string, percent: number): string => {
    // Simple color darkening (hex only)
    if (!color.startsWith('#')) return color;

    const num = parseInt(color.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
    const B = Math.max(0, (num & 0x0000ff) - amt);

    return `#${(
      0x1000000 +
      R * 0x10000 +
      G * 0x100 +
      B
    ).toString(16).slice(1)}`;
  };

  const lighten = (color: string): string => {
    // Create a very light version for backgrounds
    if (!color.startsWith('#')) return color;

    const num = parseInt(color.slice(1), 16);
    const R = Math.min(255, ((num >> 16) & 0xff) + 200);
    const G = Math.min(255, ((num >> 8) & 0xff) + 200);
    const B = Math.min(255, (num & 0xff) + 200);

    return `#${(
      0x1000000 +
      R * 0x10000 +
      G * 0x100 +
      B
    ).toString(16).slice(1)}`;
  };

  return `
    :host {
      --sitegpt-primary: ${primaryColor};
      --sitegpt-primary-hover: ${darken(primaryColor, 15)};
      --sitegpt-primary-light: ${lighten(primaryColor)};
    }
  `;
}
